import {
  getAnalytics,
  logEvent as firebaseLogEvent,
  setCurrentScreen as firebaseSetCurrentScreen,
  setUserId as firebaseSetUserId,
  setUserProperties as firebaseSetUserProperties,
} from 'firebase/analytics'
import { useMemo, useCallback } from 'react'
import { initializeFirebase } from '../../firebase'
import { AnalyticsHook } from '../types'

/**
 * useFirebaseAnalytics
 *
 * Hook for Firebase Analytics (cloud deployments).
 * Provides analytics tracking using Google Firebase Analytics.
 */
export default function useFirebaseAnalytics(): AnalyticsHook {
  const analytics = useMemo(() => {
    // Ensure Firebase app is initialized before getting analytics
    const app = initializeFirebase()
    return getAnalytics(app)
  }, [])

  const logEvent = useCallback(
    (eventName: string, params?: Record<string, unknown>): void => {
      firebaseLogEvent(analytics, eventName, params)
    },
    [analytics]
  )

  const setCurrentScreen = useCallback(
    (screenName: string): void => {
      firebaseSetCurrentScreen(analytics, screenName)
    },
    [analytics]
  )

  const setUserId = useCallback(
    (userId: string): void => {
      firebaseSetUserId(analytics, userId)
    },
    [analytics]
  )

  const setUserProperties = useCallback(
    (properties: Record<string, string>): void => {
      firebaseSetUserProperties(analytics, properties)
    },
    [analytics]
  )

  return useMemo(
    () => ({
      logEvent,
      setCurrentScreen,
      setUserId,
      setUserProperties,
    }),
    [logEvent, setCurrentScreen, setUserId, setUserProperties]
  )
}
